import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UniversalItem } from '../../../../../Catalog.types';
import { ProductGridItem } from '../../../../ProductGrid/ProductGrid.types';
import { useValuePropCardContext } from '../ValuePropCard.provider';
import { ValuePropCardDetails } from '../ValuePropCard.types';
import { useHistory } from '@shared/core';
import { useCatalogLoggingContext } from '@apps/registry/common/components/Catalog/Catalog.logging';
import { AdjustableBrandCard } from '@apps/registry/admin/components/BrandCard/BrandCard';
import { Box } from '@withjoy/joykit';
import { ValuePropCard } from '../ValuePropCard';

export const useValuePropCards = ({
  categoryLocator,
  products,
  shouldShowCustomCashTile,
  shouldDisplayValuePropCards
}: {
  categoryLocator: Maybe<string>;
  products: ReadonlyArray<UniversalItem>;
  shouldShowCustomCashTile?: boolean;
  shouldDisplayValuePropCards?: boolean;
}): { productGridItems: ProductGridItem[] } => {
  const { getValuePropCardSlots, selectedBrandPromo } = useValuePropCardContext();
  const { trackValuePropClicked } = useCatalogLoggingContext();
  const [valuePropCardsSlots, setValuePropCardsSlots] = useState<Map<number, ValuePropCardDetails>>(new Map()); // slotIndex -> card details
  const history = useHistory();

  useEffect(() => {
    const valuePropCards = getValuePropCardSlots();
    setValuePropCardsSlots(valuePropCards);

    // need the category locator to recompute the cards slots when the category changes
    // getValuePropCardSlots is not a dependency because we want to compute the slots only when we load the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryLocator]);

  const getValuePropCard = useCallback(
    (slot: number) => {
      const valuePropCard = valuePropCardsSlots.get(slot);
      if (!valuePropCard) {
        return;
      }
      if (valuePropCard.id === 'brandCard') {
        return (
          <Box position="relative">
            <AdjustableBrandCard showSponsoredText={false} brandPromo={selectedBrandPromo} brandCardType="productGrid" desktopMinWidth={320} />
          </Box>
        );
      }
      return (
        <ValuePropCard
          key={slot}
          id={valuePropCard.id}
          desktopImageUrl={valuePropCard.desktopImageUrl}
          mobileImageUrl={valuePropCard.mobileImageUrl}
          onClick={
            valuePropCard.link
              ? () => {
                  trackValuePropClicked({
                    method: 'grid',
                    title: valuePropCard.title,
                    target: valuePropCard.link,
                    productCatalogItemPosition: slot
                  });
                  return valuePropCard.isExternal ? window.open(valuePropCardsSlots.get(slot)?.link, '_blank') : history.push(valuePropCard.link as string);
                }
              : undefined
          }
        />
      );
    },
    [valuePropCardsSlots, selectedBrandPromo, trackValuePropClicked, history]
  );

  const productGridItems: ProductGridItem[] = useMemo(() => {
    const productsQueue: UniversalItem[] = [...products];
    const productGridItems: ProductGridItem[] = [];

    let visibleCardsCount = 0;
    valuePropCardsSlots.forEach((_value, slot) => {
      if (slot < products.length) {
        visibleCardsCount++;
      }
    });

    Array.from(Array(products.length + visibleCardsCount)).map((_, idx) => {
      const valuePropIndex = shouldShowCustomCashTile ? idx + 1 : idx;
      if (valuePropCardsSlots.has(valuePropIndex)) {
        productGridItems.push({
          type: 'valueProp',
          valueProp: getValuePropCard(valuePropIndex)
        });
      } else {
        const product = productsQueue.shift();
        if (product) {
          productGridItems.push({
            type: 'product',
            product
          });
        }
      }
    });
    return productGridItems;
  }, [products, valuePropCardsSlots, shouldShowCustomCashTile, getValuePropCard]);

  if (!shouldDisplayValuePropCards) {
    return {
      productGridItems: products.map(product => ({ type: 'product', product }))
    };
  }

  return { productGridItems };
};
